import { axiosInstance } from '../utils/axios';

import { contentType, requestHeaders } from '../constants/requestHeaders';

export const getChannelWithUserData = (
  { path: { orgId, streamId, channelId }, params = {} },
  extraData,
) =>
  Promise.all([
    // get channel data
    axiosInstance.get(`${orgId}/${streamId}/${channelId}`, {
      ...extraData,
      params: {
        ...params,
      },
    }),
    // get channel userData
    axiosInstance.get(`/${orgId}/${streamId}/${channelId}/userdata`, {
      ...extraData,
      params: {
        ...params,
        refresh: Math.random(),
      },
    }),
  ]);

export const getAllChannels = ({ params: { count, offset } }, extraData) =>
  axiosInstance.get(`/channels`, {
    ...extraData,
    params: {
      ...extraData?.params,
      refresh: Math.random(),
      count,
      offset,
    },
  });

export const getTrendingChannelsWithUserdata = (_, extraData) =>
  Promise.all([
    axiosInstance.get(`/trending/channels`, {
      ...extraData,
      params: {
        refresh: Math.random(),
      },
    }),
    axiosInstance.get(`/trending/channels/userdata`, {
      ...extraData,
      params: {
        refresh: Math.random(),
      },
    }),
  ]);

export const getTrendingUsersWithUserdata = (_, extraData) =>
  Promise.all([
    axiosInstance.get(`/trending/users`, {
      ...extraData,
      params: {
        refresh: Math.random(),
      },
    }),
    axiosInstance.get(`/trending/users/userdata`, {
      ...extraData,
      params: {
        refresh: Math.random(),
      },
    }),
  ]);

export const getStreamChannels = (
  { path: { orgId, streamId }, params: { full } },
  extraData,
) =>
  axiosInstance.get(`/${orgId}/${streamId}/channels`, {
    ...extraData,
    params: {
      full,
      refresh: Math.random(),
    },
  });

export const postChannel = (
  {
    path: { orgId, streamId },
    body: {
      id,
      name,
      description,
      visible,
      restricted,
      topics,
      locations,
      boosts,
    },
    params: { isPrivate },
  },
  extraData,
) =>
  axiosInstance
    .post(
      `/${orgId}/${streamId}`,
      {
        id,
        name,
        description,
        visible,
        restricted,
        topics,
        boosts,
        locations,
      },
      {
        ...extraData,
        params: {
          private: isPrivate,
        },
      },
    )
    .then(() =>
      getChannelWithUserData({ path: { orgId, streamId, channelId: id } }),
    );

export const putChannel = (
  {
    path: { orgId, streamId, channelId },
    body: { name, description, topics, locations, boosts },
    params = {},
  },
  extraData,
) =>
  axiosInstance
    .put(
      `${orgId}/${streamId}/${channelId}`,
      { name, description, topics, locations, boosts },
      extraData,
      { params },
    )
    .then(() =>
      getChannelWithUserData({
        path: { orgId, streamId, channelId },
      }),
    );

export const getUserPermissionsForChannel = (
  { orgId, streamId, channelId },
  extraData,
) =>
  axiosInstance.get(`/${orgId}/${streamId}/${channelId}/userdata`, {
    ...extraData,
    params: {
      ...extraData?.params,
      refresh: Math.random(),
    },
  });

export const getHoveredChannel = (
  { path: { orgId, streamId, channelId }, params },
  extraData,
) =>
  axiosInstance.get(`/${orgId}/${streamId}/${channelId}/hovercard`, {
    ...extraData,
    params: {
      ...params,
      refresh: Math.random(),
    },
  });

export const postChannelProfilePic = (
  { orgId, streamId, channelId, formData },
  extraData,
) =>
  axiosInstance.post(`/${orgId}/${streamId}/${channelId}/profile`, formData, {
    ...extraData,
    headers: requestHeaders.contentType(contentType.multipartFormData),
  });

export const postChannelCoverPic = (
  { orgId, streamId, channelId, formData },
  extraData,
) =>
  axiosInstance.post(`/${orgId}/${streamId}/${channelId}/cover`, formData, {
    ...extraData,
    headers: requestHeaders.contentType(contentType.multipartFormData),
  });

export const postRSSLink = (
  {
    path: { orgId, streamId, channelId },
    body: {
      name,
      description,
      url,
      priority,
      tags,
      createPrivateItem,
      ignoreFeedTags,
    },
  },
  extraData,
) =>
  axiosInstance
    .post(
      `/${orgId}/${streamId}/${channelId}/rss`,
      {
        name,
        description,
        url,
        priority,
        tags,
        createPrivateItem,
        ignoreFeedTags,
      },
      {
        ...extraData,
      },
    )
    .then(response =>
      axiosInstance.get(response.headers.location, {
        ...extraData,
        params: {
          refresh: Math.random(),
        },
      }),
    );

export const getRSSLink = (
  { path: { orgId, streamId, channelId, linkId } },
  extraData,
) =>
  axiosInstance.get(`/${orgId}/${streamId}/${channelId}/rss/${linkId}`, {
    ...extraData,
    params: {
      refresh: Math.random(),
    },
  });

export const putRSSLink = (
  {
    path: { orgId, streamId, channelId, linkId },
    body: {
      name,
      description,
      url,
      priority,
      tags,
      createPrivateItem,
      ignoreFeedTags,
    },
  },
  extraData,
) =>
  axiosInstance
    .put(
      `/${orgId}/${streamId}/${channelId}/rss/${linkId}`,
      {
        name,
        description,
        url,
        priority,
        tags,
        createPrivateItem,
        ignoreFeedTags,
      },
      extraData,
    )
    .then(() =>
      axiosInstance.get(`/${orgId}/${streamId}/${channelId}/rss/${linkId}`, {
        ...extraData,
        params: {
          refresh: Math.random(),
        },
      }),
    );

export const getChannelBookmark = (
  { path: { orgId, streamId, channelId } },
  extraData,
) =>
  axiosInstance.get(`/${orgId}/${streamId}/${channelId}/bookmark`, extraData);

export const postChannelBookmark = (
  { path: { orgId, streamId, channelId }, params: { tags } },
  extraData,
) =>
  axiosInstance.post(`/${orgId}/${streamId}/${channelId}/bookmark`, null, {
    ...extraData,
    params: {
      tags: tags?.join(',') || null,
    },
  });

export const deleteChannelBookmark = (
  { path: { orgId, streamId, channelId } },
  extraData,
) =>
  axiosInstance.delete(
    `/${orgId}/${streamId}/${channelId}/bookmark`,
    extraData,
  );

export const postSubscribeOnChannel = (
  { path: { orgId, streamId, channelId }, body: { subscriptionId } },
  extraData,
) =>
  axiosInstance.post(
    `${orgId}/${streamId}/${channelId}/subscribe`,
    { id: subscriptionId },
    {
      ...extraData,
      params: {
        refresh: Math.random(),
      },
    },
  );

export const deleteUnsubscribeAll = (
  { path: { orgId, streamId, channelId } },
  extraData,
) =>
  axiosInstance
    .delete(`/${orgId}/${streamId}/${channelId}/subscribe`, {
      ...extraData,
      params: {
        refresh: Math.random(),
      },
    })
    .then(() =>
      getChannelWithUserData({ path: { orgId, streamId, channelId } }),
    );
